// ── Errorzen Contracts — Zod schemas (source of truth) ───────────────────────
// The OPEN wire schema for the canonical Scorpio error body: RFC 9457
// `application/problem+json`, extended with `code` / `request_id` / `trace_id`
// / `retryable` / `retry_after_seconds` / `source` / `details`. Mirrors the
// Python producer models in shared/errorzen/ — keep in sync when modifying
// either side. Spec: API Specs: errorzen v2.1 §Wire format (Epic ASC-6291).
//
// OPEN by design (RFC 9457 §3.2): a distributed MFE platform deploys
// frontends and backends independently, so a consumer bundle must tolerate a
// `code` (and extension members) it has never seen. `.passthrough()` is
// load-bearing — do NOT replace it with `.strict()`. The CLOSED
// KnownErrorCodeSchema (generated, see codes.ts) drives producer typing and
// localized copy instead.

import { z } from 'zod'

// URI-reference (RFC 3986): optional scheme followed by characters legal in a
// URI — accepts both the https form (problem type docs pages) and the urn
// form (`urn:oraczen:req:...` instances).
const URI_REFERENCE_PATTERN = /^(?:[A-Za-z][A-Za-z0-9+.-]*:)?[^\s"<>\\^`{|}]*$/

// Uppercase DOMAIN.REASON — an uppercase namespace, a dot, then a reason
// segment that may contain underscores (e.g. `LLM.PROVIDER_QUOTA_EXHAUSTED`).
const CODE_PATTERN = /^[A-Z][A-Z0-9]*\.[A-Z][A-Z0-9_]*$/

// `req_` prefix + Crockford base32 (ULID alphabet — no I, L, O, U); overall
// length is bounded 8–64 by the min/max refinements on the field.
const REQUEST_ID_PATTERN = /^req_[0-9A-HJKMNP-TV-Z]+$/

// W3C trace-id: exactly 32 lowercase hex characters.
const TRACE_ID_PATTERN = /^[0-9a-f]{32}$/

export const ProblemDetailsWireSchema = z
  .object({
    type: z.string().min(1).regex(URI_REFERENCE_PATTERN),
    title: z.string(),
    status: z.number().int().gte(400).lte(599),
    detail: z.string().optional(),
    instance: z.string().min(1).regex(URI_REFERENCE_PATTERN).optional(),
    code: z.string().regex(CODE_PATTERN),
    request_id: z.string().min(8).max(64).regex(REQUEST_ID_PATTERN),
    trace_id: z.string().regex(TRACE_ID_PATTERN).nullable().optional(),
    retryable: z.boolean(),
    retry_after_seconds: z.number().int().nonnegative().nullable().optional(),
    source: z.string().nullable().optional(),
    details: z.record(z.unknown()).optional(),
  })
  .passthrough()

export type ProblemDetails = z.infer<typeof ProblemDetailsWireSchema>
